import { useEffect, useRef, useCallback } from 'react';

const useRetellWidget = () => {
  const scriptInjected = useRef(false);
  const readyRef = useRef(false);

  const findLauncher = () => {
    // More comprehensive launcher search
    const selectors = [
      '[id^="retell-widget-launcher"]',
      '[data-retell-widget-launcher]',
      'button[aria-label="Open chat"]',
      '[data-retell-widget]',
      '.retell-widget-launcher',
      '[data-retell-widget-button]',
      'iframe[src*="retell"]',
      '[class*="retell"]',
      '[id*="retell"]'
    ];
    
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        console.log(`[Retell] Found launcher with selector: ${selector}`, element);
        return element;
      }
    }
    
    console.log('[Retell] No launcher found with any selector');
    return null;
  };

  useEffect(() => {
    // SSR guard
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (scriptInjected.current) return;

    console.log('[Retell] Starting widget initialization...');

    // If already present, mark as injected and bail
    if (document.getElementById('retell-widget')) {
      scriptInjected.current = true;
      readyRef.current = true;
      console.log('[Retell] Widget script already exists, marking as ready');
    } else {
      const script = document.createElement('script');
      script.id = 'retell-widget';
      script.type = 'module';
      script.src = 'https://dashboard.retellai.com/retell-widget.js';

      // IMPORTANT: Replace with your actual Retell credentials
      // Get these from your Retell dashboard: https://dashboard.retellai.com/
      script.setAttribute('data-public-key', 'public_key_b3f388a1c54f3768d5320');
      script.setAttribute('data-agent-id', 'agent_2c5181dfc306960dbf60f37d0a');
      script.setAttribute('data-title', 'Painttura Free Estimator');
      script.setAttribute('data-logo-url', 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752951692574-PAINTTURA%20%283%29.png');
      script.setAttribute('data-color', '#2563EB');
      
      // Add greeting message
      script.setAttribute('data-greeting-message', 'Hello 👋 Say "Hello" to get started on your free estimate');

      script.onload = () => {
        console.log('[Retell] ✅ Script loaded successfully');
        
        // Wait a bit for the widget to initialize
        setTimeout(() => {
          const launcher = findLauncher();
          if (launcher) {
            readyRef.current = true;
            console.log('[Retell] ✅ Widget is ready!');
          } else {
            console.log('[Retell] ⚠️ Widget loaded but launcher not found yet, will retry...');
            
            // Set up observer to watch for the launcher
            const observer = new MutationObserver((mutations) => {
              const launcher = findLauncher();
              if (launcher) {
                readyRef.current = true;
                console.log('[Retell] ✅ Launcher found via DOM observer!');
                observer.disconnect();
              }
            });

            observer.observe(document.body, { 
              childList: true, 
              subtree: true,
              attributes: true
            });

            // Force ready after 5 seconds regardless
            setTimeout(() => {
              readyRef.current = true;
              observer.disconnect();
              console.log('[Retell] ⏰ Forcing ready state after timeout');
            }, 5000);
          }
        }, 1000);
      };

      script.onerror = (error) => {
        console.error('[Retell] ❌ Failed to load script:', error);
        readyRef.current = true; // Still mark as ready to prevent blocking
      };

      document.head.appendChild(script);
      scriptInjected.current = true;
      console.log('[Retell] 📝 Script injection initiated');
    }

    return () => {};
  }, []);

  const openWidget = useCallback(() => {
    console.log('[Retell] 🚀 openWidget called');
    console.log('[Retell] Status check:', {
      scriptInjected: scriptInjected.current,
      isReady: readyRef.current,
      windowRetell: !!window.Retell,
      launcher: !!findLauncher()
    });

    const tryOpen = (attempt = 1) => {
      console.log(`[Retell] Attempt ${attempt} to open widget...`);

      // Method 1: Try to find and click the launcher
      const launcher = findLauncher();
      if (launcher instanceof HTMLElement) {
        console.log('[Retell] ✅ Method 1: Clicking launcher element');
        try {
          launcher.click();
          return true;
        } catch (error) {
          console.error('[Retell] ❌ Error clicking launcher:', error);
        }
      }

      // Method 2: Try window.Retell API
      if (typeof window !== 'undefined' && window.Retell) {
        console.log('[Retell] ✅ Method 2: Using window.Retell API');
        try {
          if (typeof window.Retell.open === 'function') {
            window.Retell.open();
            return true;
          }
          if (typeof window.Retell.show === 'function') {
            window.Retell.show();
            return true;
          }
          if (typeof window.Retell.toggle === 'function') {
            window.Retell.toggle();
            return true;
          }
          if (typeof window.Retell === 'function') {
            window.Retell();
            return true;
          }
        } catch (error) {
          console.error('[Retell] ❌ Error with Retell API:', error);
        }
      }

      // Method 3: Try custom events
      if (typeof window !== 'undefined') {
        console.log('[Retell] ✅ Method 3: Dispatching events');
        try {
          window.dispatchEvent(new CustomEvent('retell:open'));
          window.dispatchEvent(new CustomEvent('retell-open'));
          window.postMessage({ type: 'RETELL_OPEN' }, '*');
          window.postMessage({ action: 'open-retell' }, '*');
        } catch (error) {
          console.error('[Retell] ❌ Error dispatching events:', error);
        }
      }

      // Method 4: Try to find iframe and send message
      const iframe = document.querySelector('iframe[src*="retell"]');
      if (iframe && iframe.contentWindow) {
        console.log('[Retell] ✅ Method 4: Sending message to iframe');
        try {
          iframe.contentWindow.postMessage({ action: 'open' }, '*');
        } catch (error) {
          console.error('[Retell] ❌ Error messaging iframe:', error);
        }
      }

      console.log(`[Retell] ⚠️ Attempt ${attempt} completed, no guaranteed success`);
      return false;
    };

    // Try immediately
    const success = tryOpen(1);
    
    // If not successful, retry with delays
    if (!success) {
      setTimeout(() => tryOpen(2), 500);
      setTimeout(() => tryOpen(3), 1500);
      setTimeout(() => tryOpen(4), 3000);
    }
  }, []);

  // Global debugging functions
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.openRetellWidget = openWidget;
      window.debugRetell = () => {
        console.log('=== RETELL DEBUG INFO ===');
        console.log('Script injected:', scriptInjected.current);
        console.log('Is ready:', readyRef.current);
        console.log('Window.Retell:', window.Retell);
        console.log('Launcher element:', findLauncher());
        console.log('All elements with retell in id/class:');
        
        // Find all elements that might be related to Retell
        const retellElements = document.querySelectorAll('*[id*="retell"], *[class*="retell"], iframe[src*="retell"]');
        retellElements.forEach((el, i) => {
          console.log(`  ${i + 1}:`, el);
        });
        
        console.log('=== END DEBUG INFO ===');
        return {
          scriptInjected: scriptInjected.current,
          isReady: readyRef.current,
          launcher: findLauncher(),
          retellGlobal: window.Retell,
          allRetellElements: retellElements
        };
      };
      
      return () => {
        delete window.openRetellWidget;
        delete window.debugRetell;
      };
    }
  }, [openWidget]);

  return { openWidget, isReady: readyRef.current };
};

export default useRetellWidget;